import { render, html } from "lit";
import { dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { TreeAdapter, TreeSource, TreeOptions, NodeSource } from "@omegagrid/tree";
import { CellModel, ColumnSource, renderers, RowSource } from "@omegagrid/grid";
import rp, { DEFAULT_GRID_EDITOR_OPTIONS, GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { EntityAccess, DEFAULT_ENTITY_ACCESS_CHANGE, EntityAccessType } from "@reporting/rp-types/users";
import { UserTreeAdapter } from "../adapters";
import { EntityAccessExt } from "../types";

// const BG_RED = ['#a13f3f', '#ffffff'];
// const BG_GREEN = ['#6eef94', '#000000ff'];
const COLOR_GREEN = '#008000';
const COLOR_RED = '#c30000ff';

class EntityTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private a: number,
		private entity: string,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		this.items = await rp.ms('users').get<NodeSource[]>('/entityAccess/entityItems', {
			entity: this.entity,
			accounting: this.a
		});
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}

}

export class EntityAccessGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	constructor(
		private a: number,
	) { super() }

	attached() {
		dom.appendStyle(this.component.table.shadowRoot, `
			a:hover { text-decoration: underline; cursor: pointer; }
		`, '__rp-entity-access-grid-editor-style');
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		const types = await rp.ms('users').get<{entity: string, description: string}[]>('/entityAccess/userTypes', {
			accounting: this.a,
		});

		this._columns = [{
			data: '_subject',
			header: msg('Subject'),
			levelIndent: true,
			e: false,
			filter: false,
			renderer: this.subjectRenderer,
			w: 450,
			priority: 1,
		}, {
			data: 'allow',
			header: msg('Allowed'),
			t: 'b',
			e: true,
			w: 100,
			l: 0,
			collapsed: true,
		}, {
			data: 'entity',
			header: msg('Type'),
			t: 's',
			content: types.map(t => ({ k: t.entity, v: msg(t.description) })),
			e: false,
			w: 200,
			l: 1,
		}, {
			data: 'name',
			header: msg('Entity'),
			t: 't',
			e: false,
			w: 200,
			l: 1,
		}, {
			data: '_user_role',
			header: `${msg('User')}/${msg('Role')}` ,
			t: 't',
			e: false,
			w: 180,
			l: 1,
		}, {
			data: 'who_created',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 180,
			l: 1,
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145,
			l: 1,
		}, { // hidden columns for internal use
			data: 'id',
			t: 't',
			e: false,
			hidden: true,
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true,
		}, {
			data: 'level',
			t: 'n',
			e: false,
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]>{
		const entities = await rp.ms('users').get<EntityAccess[]>('/entityAccess/userTree', {
			accounting: this.a,
		});

		const rows = entities.map<RowSource>(data => ({ l: data.level, collapsed: false, data: {
			...data,
			_subject: { props: data, v: this.getSubjectValue(data) },
			allow: {
				v: data.allow,
				e: data.level === 0 ? false : // always non-editable for entity type level
					data.level === 1 ? DEFAULT_ENTITY_ACCESS_CHANGE[data.entity as EntityAccessType] ?? false : // editable based on entity type
					true, // always editable for user/role level
			},
			name: data.level > 0 ? data.name : null,
			_user_role: data.role ? `[R] ${data.role}` : data.login_name ?? data.user_name,
		}}));

		return rows;
	}

	getSubjectValue(data: EntityAccess): string {
		/* row levels:
		0 - entity type (hardcoded in ms)
		1 - entity (default access - from DB or inherited from higher level)
		2 - User / Role (specific access - from DB)
		*/
		return (
			data.level === 2 ? data.role ?? (data.login_name ?? data.user_name) :
			data.level === 1 ? `${data.name} [${data.id}]` : msg(data.name)
		) as string;
	}

	subjectRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		const isNew = !!cell.row.getProperty('new');
		if (isNew) {
			div = renderers.DropdownRenderer(div, cell);
		} else {
			div = renderers.BaseRenderer(div, cell);
		}

		const data = cell.properties as EntityAccess;
		if (!data) return div;

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.paddingLeft = dom.px(cell.calculatedIndent ?? 4);
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '2px';
		innerDiv.style.alignItems = 'center';

		const rowLvl = data.level;
		const value = this.getSubjectValue(data);

		render(html`
			${rowLvl === 2 ? html`<og-icon icon="${data.role ? 'users' : 'user'}"></og-icon>` : ''}
			<div style="flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
				${rowLvl === 1 ? html`
					<a @pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
						@click="${() => {
							rp.host.open(
								`users-entityUsers-${data.entity}-${data.id}`,
								{ title: `${msg('Access')}: ${data.name}` }
							);
						}}">${value}</a>
				` : value}
			</div>
			${!isNew ? rowLvl === 2 ? html`
				<og-button
					icon="trash"
					title="${msg('Delete')}"
					color="red"
					style="height: 15px"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.deleteAccess(cell)}">
				</og-button>
			` : html`
				<og-button
					icon="plus"
					title="${rowLvl === 1 ? `${msg('User')}/${msg('Role')}` : msg('Entity')}"
					color="${rowLvl === 1 ? 'blue' : 'gray'}"
					style="height: 15px"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => rowLvl === 1 ? this.addAccess(cell) : this.addEntity(cell) }">
				</og-button>
			` : ''}
		`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	addEntity(cell: CellModel) {
		const newRow = this.editor.newRow(cell.r + 1);
		newRow.level = cell.row.level + 1;
		newRow.data = {
			allow: cell.row.data.allow,
			entity: cell.row.data.entity,
			level: 1,
		};
		const cell1 = newRow.cell(0); // subject cell
		cell1.type = 's';
		cell1.editable = true;
		cell1.adapter = new EntityTreeAdapter(this.a, cell.row.data.entity, { search: true, selectMode: 'single', allowEmpty: false });
		newRow.cell(1).editable = DEFAULT_ENTITY_ACCESS_CHANGE[cell.row.data.entity as EntityAccessType] ?? false; // allow cell
		this.component.rendering.updateCell(cell1);
	}

	addAccess(cell: CellModel) {
		const newRow = this.editor.newRow(cell.r + 1);
		newRow.level = cell.row.level + 1;
		newRow.data = {
			allow: !cell.row.data.allow,
			entity: cell.row.data.entity,
			id: cell.row.data.id,
			level: 2,
		};
		const cell1 = newRow.cell(0); // subject cell
		cell1.type = 's';
		cell1.editable = true;
		cell1.adapter = new UserTreeAdapter(true, { search: true, selectMode: 'single', allowEmpty: false });
		this.component.rendering.updateCell(cell1);
	}

	deleteAccess(cell: CellModel) {
		this.editor.deleteRow(cell.r);
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: false,
			filters: true,
			pageSize: null,
			refreshAfterSave: true,
			saveMode: 'edited_rows',
			exportTypes: ['xlsx'],
			toolbar: {
				new: { enabled: false },
				export: {
					enabled: true,
					items: [
						{ key: 'export.xlsx', value: 'XLSX', icon: 'file-excel' },
					],
				},
			},
			afterRenderer: (div: HTMLDivElement, cell: CellModel) => {
				// colorize only 1st column for level 2 rows || cell.row.data.level !== 2
				if (cell.column.index !== 0 || !cell.row.data) {
					return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
				}

				const allowCell = cell.row.cell(1);
				div.style.color = allowCell.value ? COLOR_GREEN : COLOR_RED;

				// if (allowCell.value) {
				// 	div.style.backgroundColor = BG_GREEN[0];
				// 	div.style.color = BG_GREEN[1];
				// } else if (!allowCell.value) {
				// 	div.style.backgroundColor = BG_RED[0];
				// 	div.style.color = BG_RED[1];
				// }

				return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
			}
		});
	}

	async save(rowData: RowData[]) {
		let error = false;
		const data = rowData.map(row => {
			const allow = row.data['allow'] ? true : false;
			// new rows
			if (row.state === 'new') {
				if (row.data['_subject'] === undefined) {
					rp.host.bucket.showAlert({content: msg('Subject cannot be empty!'), type: 'warning', title: msg('Validation Error')});
					error = true;
					return;
				}
				const subject = String(row.data['_subject']);
				const lvl = Number(row.data['level']);
				return {
					_state: row.state,
					entity: String(row.data['entity']),
					id: lvl === 1 ? subject : row.data['id'], // for entity level, take id from subject
					id_access_role: lvl === 2 && subject.startsWith('r') ? parseInt(subject.substring(1)) : null,
					user_name: lvl === 2 && subject.startsWith('u') ? subject.substring(1) : null,
					allow,
				} as EntityAccessExt;
			}
			// edited/deleted rows
			if (row.data['idz'] && Number(row.data['idz']) > 0) { // existing rows from DB
				return {
					_state: row.state,
					idz: Number(row.data['idz']),
					allow,
				} as EntityAccessExt;
			} else { // fake default rows added in ms
				return {
					_state: row.state,
					entity: String(row.data['entity']),
					id: String(row.data['id']),
					allow,
				} as EntityAccessExt;
			}
		});

		if (error) return Promise.reject('Validation error');
		await rp.ms('users').post(`entityAccess`, data, {
			accounting: this.a,
		});
		// set filters collapsed state (allow column) to preserve it after refresh
		this._columns[1].collapsed = this.editor.grid.model.columns[1].collapsed;
	}

}
